import { Fragment, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Truck, PackageCheck, Warehouse, Hourglass, Building2, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { mediaUrl } from "./ProductsPage";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-stone-200 bg-white p-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">{label}</p>
      <Icon size={14} className="text-[#1C4E35]" />
    </div>
    <p className="mt-1 font-heading text-xl font-black text-[#0A0D0B]">{value}</p>
  </div>
);

const StatusBadge = ({ status }) =>
  status === "pending" ? (
    <span className="border border-amber-300 bg-amber-50 px-1.5 py-px text-[9px] font-medium text-amber-700">Onay bekliyor</span>
  ) : (
    <span className="border border-green-300 bg-green-50 px-1.5 py-px text-[9px] font-medium text-green-700">Mal kabul edildi</span>
  );

export default function SupplierTrackingPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");
  const canRemove = user?.role === "admin" || user?.role === "satinalma";
  const [removeTarget, setRemoveTarget] = useState(null);

  const removeAssignment = async () => {
    const p = removeTarget;
    setRemoveTarget(null);
    try {
      const r = await api.post("/procurement/unassign-product", { sku: p.sku, variant: p.variant || null, reset_deliveries: true });
      toast.success(`${p.name}: ${r.data.removed} atama kaldırıldı`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaldırılamadı");
    }
  };

  const load = () => {
    api.get("/procurement/supplier-summary")
      .then((r) => { setRows(r.data.data); setTotals(r.data.totals); })
      .catch(() => toast.error("Veriler yüklenemedi"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (user && !["admin", "satinalma", "malkabul"].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (loading) return <p className="text-sm text-stone-500" data-testid="tracking-loading">Yükleniyor...</p>;

  const ql = q.trim().toLocaleLowerCase("tr");
  const filteredRows = ql
    ? rows
        .map((s) => {
          if ((s.name || "").toLocaleLowerCase("tr").includes(ql)) return s;
          const prods = s.products.filter((p) => (p.name || "").toLocaleLowerCase("tr").includes(ql) || (p.sku || "").toLowerCase().includes(ql));
          return prods.length ? { ...s, products: prods, productCount: prods.length } : null;
        })
        .filter(Boolean)
    : rows;
  const isOpen = (s) => (ql ? true : open[s.id]);

  return (
    <div className="space-y-5 fade-up" data-testid="supplier-tracking-page">
      {totals && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Kpi icon={Building2} label="Tedarikçi" value={totals.supplierCount} testId="tracking-kpi-suppliers" />
          <Kpi icon={Truck} label="Satınalınan Miktar" value={totals.assignedQty} testId="tracking-kpi-assigned" />
          <Kpi icon={PackageCheck} label="Ürün Çeşidi" value={rows.reduce((a, s) => a + s.productCount, 0)} testId="tracking-kpi-products" />
        </div>
      )}

      <div className="relative w-full sm:w-96">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tedarikçi veya ürün adı / SKU ara..." className="h-9 rounded-sm border-stone-300 bg-white pl-8" data-testid="tracking-search-input" />
      </div>

      <div className="border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs">Tedarikçi</TableHead>
              <TableHead className="text-right text-xs">Ürün</TableHead>
              <TableHead className="text-right text-xs">Satınalma Adedi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="py-6 text-center text-sm text-stone-400" data-testid="tracking-empty">
                  {ql ? "Aramanızla eşleşen tedarikçi veya ürün bulunamadı." : "Henüz tedarikçi ataması yapılmadı."}
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((s) => (
              <Fragment key={s.id}>
                <TableRow
                  onClick={() => setOpen((o) => ({ ...o, [s.id]: !o[s.id] }))}
                  data-testid={`tracking-row-${s.id}`}
                  className="cursor-pointer hover:bg-stone-50"
                >
                  <TableCell className="py-2.5">
                    <div className="flex items-center gap-2">
                      {isOpen(s) ? <ChevronDown size={14} className="text-stone-400" /> : <ChevronRight size={14} className="text-stone-400" />}
                      <span className="text-sm font-medium text-[#0A0D0B]">{s.name}</span>
                      {s.direct && <span className="border border-sky-300 bg-sky-50 px-1.5 py-px text-[9px] font-medium text-sky-700">Doğrudan Satınalma</span>}
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-right text-sm">{s.productCount}</TableCell>
                  <TableCell className="py-2.5 text-right text-sm font-medium">{s.assignedQty}</TableCell>
                </TableRow>
                {isOpen(s) && s.products.map((p, pi) => (
                  <TableRow key={`${p.sku}|${p.variant || ""}`} className="bg-stone-50/70" data-testid={`tracking-product-row-${s.id}-${pi}`}>
                    <TableCell className="py-2 pl-10">
                      <div className="flex items-center gap-2.5">
                        {p.image ? (
                          <img src={mediaUrl(p.image)} alt={p.name} loading="lazy" className="h-10 w-10 shrink-0 border border-stone-200 bg-white object-contain" data-testid={`tracking-product-image-${s.id}-${pi}`} />
                        ) : (
                          <div className="h-10 w-10 shrink-0 border border-stone-100 bg-white" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#0A0D0B]">
                            {p.name}
                            {p.orphan && (
                              <span className="ml-1.5 border border-red-300 bg-red-50 px-1.5 py-px text-[9px] font-medium text-red-700" title="Bu ürün artık canlı siparişlerde yer almıyor; sipariş iptal edilmiş olabilir" data-testid={`tracking-orphan-${s.id}-${pi}`}>
                                Siparişte Yok
                              </span>
                            )}
                          </p>
                          <p className="text-[10px] text-stone-500">{p.sku}{p.variant ? ` · ${p.variant}` : ""}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      {canRemove && (
                        <button
                          type="button"
                          onClick={() => setRemoveTarget(p)}
                          title="Atamayı kaldır"
                          data-testid={`tracking-remove-${s.id}-${pi}`}
                          className="inline-flex items-center gap-1 border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 transition-colors hover:bg-red-600 hover:text-white active:scale-95"
                        >
                          <Trash2 size={10} /> Atamayı Kaldır
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-right text-sm font-semibold">{p.assignedQty}</TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent className="rounded-sm border-stone-200" data-testid="tracking-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-base font-bold tracking-tight">Atamayı kaldır</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-stone-600">
              {removeTarget && `${removeTarget.name} (${removeTarget.sku}) için TÜM tedarikçi atamaları ve varsa teslimat kayıtları kaldırılacak. Emin misiniz?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-stone-300">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={removeAssignment} data-testid="tracking-remove-ok" className="rounded-sm bg-red-600 text-white hover:bg-red-700 active:scale-95">
              Evet, kaldır
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
